import { useState, useEffect } from "react";
import { IoMdPerson, IoMdCalendar, IoMdBriefcase, IoMdCheckmark, IoMdCall, IoMdMail, IoMdArrowBack, IoMdArrowForward, IoMdClose } from "react-icons/io";
import { dashboardAPI, apiRequest } from "services/apisService";
import { Link } from 'react-router-dom';
import CustomSelect from "components/Dashboard/CustomSelect";
import KPIModal from "components/Dashboard/KPIModal";

const StatCard = ({ icon: Icon, count, title, bgColor, iconColor, linkTo, isYellow = false }) => (
  <Link to={linkTo} className="block">
    <div className="rounded-2xl bg-white p-6 shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-100 relative overflow-hidden cursor-pointer">
      <div 
        className={`absolute top-0 right-0 w-20 h-20 rounded-full ${isYellow ? 'bg-yellow-100 opacity-30' : 'opacity-5'}`}
        style={{ backgroundColor: !isYellow ? bgColor : undefined, transform: 'translate(25%, -25%)' }}
      />
      <div className="flex items-center justify-between mb-4 relative z-10">
        <div 
          className={`rounded-xl p-4 shadow-sm ${isYellow ? 'bg-yellow-100' : ''}`}
          style={{ backgroundColor: !isYellow ? `${bgColor}1A` : undefined }}
        >
          <Icon className={`h-8 w-8 ${isYellow ? 'text-yellow-600' : ''}`} style={{ color: !isYellow ? iconColor : undefined }} />
        </div>
        <div className="text-right">
          <h3 className="text-[2.4rem] font-bold text-gray-800">{count}</h3>
        </div>
      </div>
      <p className="text-gray-600 text-lg font-medium relative z-10">{title}</p>
    </div>
  </Link>
);

const ActivityCard = ({ icon: Icon, count, title, subtitle, linkTo, isYellow = false, bgColor }) => (
  <Link to={linkTo} className="block">
    <div className="rounded-2xl bg-white p-8 shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-100 relative overflow-hidden">
      <div 
        className={`absolute top-0 right-0 w-24 h-24 rounded-full ${isYellow ? 'bg-yellow-100 opacity-50' : 'opacity-5'}`}
        style={{ backgroundColor: !isYellow ? bgColor : undefined, transform: 'translate(30%, -30%)' }}
      />
      <div className="flex items-center justify-between mb-6 relative z-10">
        <div 
          className={`rounded-xl p-5 shadow-sm ${isYellow ? 'bg-yellow-100' : ''}`}
          style={{ backgroundColor: !isYellow ? `${bgColor}1A` : undefined }}
        >
          <Icon className={`h-8 w-8 ${isYellow ? 'text-yellow-600' : ''}`} style={{ color: !isYellow ? bgColor : undefined }} />
        </div>
        <div className="text-right">
          <h3 className="text-5xl font-bold text-gray-800">{count}</h3>
        </div>
      </div>
      <div className="flex items-center justify-between relative z-10">
        <p className="text-gray-600 text-xl font-medium">{title}</p>
      </div>
    </div>
  </Link>
);

const MonthYearFilter = ({ selectedMonth, onMonthChange }) => {
  const currentYear = new Date().getFullYear(); 
  const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0'); 
  const [selectedMonthOnly, setSelectedMonthOnly] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  useEffect(() => {
    if (selectedMonth) {
      const [year, month] = selectedMonth.split('-');
      setSelectedYear(parseInt(year));
      setSelectedMonthOnly(month);
    } else {
      const currentValue = `${currentYear}-${currentMonth}`;
      onMonthChange(currentValue);
    }
  }, [selectedMonth, currentYear, currentMonth, onMonthChange]);

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const years = [
    { value: currentYear, label: currentYear.toString() },
    { value: currentYear + 1, label: (currentYear + 1).toString() }
  ];

  const handleMonthChange = (month) => {
    setSelectedMonthOnly(month);
    if (month) {
      const combinedValue = `${selectedYear}-${month}`;
      onMonthChange(combinedValue);
    } else {
      onMonthChange('');
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(parseInt(year));
    if (selectedMonthOnly) {
      const combinedValue = `${year}-${selectedMonthOnly}`;
      onMonthChange(combinedValue);
    }
  };

  return (
    <div className="mb-6 flex gap-4">
      {/* Month Dropdown */}
      <div className="w-48">
        <CustomSelect
          value={selectedMonthOnly}
          onChange={(e) => handleMonthChange(e.target.value)}
          options={months}
          placeholder="Choose a month"
          name="monthFilter"
        />
      </div>
      
      {/* Year Dropdown */}
      <div className="w-32">
        <CustomSelect
          value={selectedYear}
          onChange={(e) => handleYearChange(e.target.value)}
          options={years}
          placeholder="Year"
          name="yearFilter"
        />
      </div>
    </div>
  );
};

const UsersTable = ({ users, loading, onSetKPI }) => {
 const formatCurrency = (amount) => {
  return `₹ ${new Intl.NumberFormat('en-IN').format(amount)}`;
};

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#3985b6]"></div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white shadow-md border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead style={{ backgroundColor: '#3985b6' }}>
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white border-b border-gray-200">Name</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white border-b border-gray-200">Email</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white border-b border-gray-200">Phone Number</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white border-b border-gray-200">Target</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white border-b border-gray-200">Achieved</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-white border-b border-gray-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map((user, index) => (
                <tr key={user.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors duration-150`}>
                  <td className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full flex items-center justify-center mr-3 text-white font-semibold text-sm" style={{ backgroundColor: '#3985b6' }}>
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-gray-800 font-medium">{user.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 border-b border-gray-200">
                    <span className="text-gray-700 font-medium">{user.email}</span>
                  </td>
                  <td className="px-6 py-4 border-b border-gray-200">
                    <span className="text-gray-700 font-medium">{user.phoneNumber || '-'}</span>
                  </td>
                 <td className="px-6 py-4 border-b border-gray-200">
                  <span className="text-gray-800 font-semibold bg-blue-50 px-2 py-1 rounded">
                    {user.target ? formatCurrency(user.target.amount) : '-'}
                  </span>
                </td>
                <td className="px-6 py-4 border-b border-gray-200">
                  <span className="text-gray-800 font-semibold bg-green-50 px-2 py-1 rounded">
                    {user.target ? formatCurrency(user.target.achieved) : '-'}
                  </span>
                </td>
                  <td className="px-6 py-4 border-b border-gray-200">
                    {!user.target ? (
                      <button
                        onClick={() => onSetKPI(user)}
                        className="px-3 py-1 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity duration-150"
                        style={{ backgroundColor: '#3985b6' }}
                      >
                        Set KPI
                      </button>
                    ) : (
                      <span className="text-sm text-gray-700 font-medium">Already Set</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-500">No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between mt-6">
      <div className="text-sm text-gray-500">
        Page {currentPage} of {totalPages}
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
          style={{ borderColor: currentPage === 1 ? '#e5e7eb' : '#3985b6' }}
        >
          <IoMdArrowBack className="w-4 h-4 mr-1" />
          Previous
        </button>
        
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
          if (pageNum > totalPages) return null;
          
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${
                pageNum === currentPage
                  ? 'text-white border-transparent'
                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
              style={{ 
                backgroundColor: pageNum === currentPage ? '#3985b6' : undefined,
                borderColor: pageNum === currentPage ? '#3985b6' : undefined
              }}
            >
              {pageNum}
            </button>
          );
        })}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
          style={{ borderColor: currentPage === totalPages ? '#e5e7eb' : '#3985b6' }}
        >
          Next
          <IoMdArrowForward className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const [role, setRole] = useState(user?.role);
  const [userName, setUser] = useState(user?.name);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usersData, setUsersData] = useState({ users: [], total: 0, totalPages: 1 });
  const [usersLoading, setUsersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isKPIModalOpen, setIsKPIModalOpen] = useState(false);
  const [selectedUserForKPI, setSelectedUserForKPI] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');

  const handleKPISuccess = () => {
  fetchUsersList(currentPage, selectedMonth); 
  };

  const isAdmin = role === 'ADMIN';
  const USERS_PER_PAGE = 10;

  useEffect(() => {
    fetchDashboardData();
    if (isAdmin) {
      fetchUsersList(1);
    }
  }, [isAdmin]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await dashboardAPI.getDashboardCount();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersList = async (page = 1, monthFilter = selectedMonth) => {
    try {
      setUsersLoading(true);
      let url = `/users?page=${page}&limit=${USERS_PER_PAGE}`;
      
      if (monthFilter) {
        url += `&month=${monthFilter}`;
      }
      
      const { data } = await apiRequest(url, {
        method: "GET"
      });
      setUsersData({
        users: data.data || [],
        total: data.total || 0,
        totalPages: data.totalPages || 1
      });
      setCurrentPage(page);
    } catch (err) {
      console.error("Failed to fetch users list:", err);
    } finally {
      setUsersLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= usersData.totalPages) {
      fetchUsersList(page);
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    setCurrentPage(1); 
    fetchUsersList(1, month);
  };

  const handleSetKPI = (user) => {
    setSelectedUserForKPI(user);
    setIsKPIModalOpen(true);
  };

  const handleCloseKPIModal = () => {
    setIsKPIModalOpen(false);
    setSelectedUserForKPI(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
       <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3985b6]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  const statsData = [
    { icon: IoMdPerson, count: dashboardData?.contact?.totalContacts || 0, title: "Open Contacts", bgColor: "#3985b6", iconColor: "#3985b6", linkTo: "/contact" },
    { icon: IoMdBriefcase, count: dashboardData?.deals?.totalDeals || 0, title: "Open Deals", linkTo: "/leads", isYellow: true },
    { icon: IoMdCalendar, count: dashboardData?.deals?.demoSchedule || 0, title: "Demo Scheduled", bgColor: "#3985b6", iconColor: "#3985b6", linkTo: "/leads?filter=demo_scheduled" },
    { icon: IoMdCheckmark, count: dashboardData?.deals?.closedWon || 0, title: "Closed Deals", linkTo: "/leads?filter=closed_won", isYellow: true }
];

  const contactActivityData = [
    { 
      icon: IoMdCall, 
      count: dashboardData?.followUpCount?.contact?.callCount || 0, 
      title: "Follow-up Calls", 
      subtitle: "Follow ups", 
      linkTo: "/contact?filter=followupcalls", 
      bgColor: "#3985b6" 
    },
    { 
      icon: IoMdMail, 
      count: dashboardData?.followUpCount?.contact?.mailCount || 0, 
      title: "Follow-up Mails", 
      subtitle: "Follow ups", 
      linkTo: "/contact?filter=followupmails", 
      isYellow: true 
    }
  ];

 const leadActivityData = [
  { 
    icon: IoMdCall, 
    count: dashboardData?.followUpCount?.lead?.callCount || 0, 
    title: "Follow-up Calls", 
    subtitle: "Follow ups", 
    linkTo: "/leads?filter=followupcalls", 
    bgColor: "#3985b6" 
  },
  { 
    icon: IoMdMail, 
    count: dashboardData?.followUpCount?.lead?.mailCount || 0, 
    title: "Follow-up Mails", 
    subtitle: "Follow ups", 
    linkTo: "/leads?filter=followupmails", 
    isYellow: true 
  }
];

  return (
    <div className="p-6">
      {/* User Welcome Section */}
      <div className="flex items-center mb-8 mt-2">
        <div className="h-16 w-16 rounded-full flex items-center justify-center mr-4 shadow-md" style={{ backgroundColor: '#3985b6' }}>
          <IoMdPerson className="h-10 w-10 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Welcome back, {userName}</h1>
          <p className="text-gray-600 text-lg">Here's what's happening with your business today</p>
        </div>
      </div>

      {/* Overall Statistics Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <div className="w-1 h-8 mr-3 rounded-full" style={{ backgroundColor: '#3985b6' }}></div>
          Overall Statistics
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {statsData.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>
      </div>

      {/* Conditional Section: Today's Activity OR Users Management */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <div 
            className="w-1 h-8 mr-3 rounded-full" 
            style={{ backgroundColor: isAdmin ? '#3985b6' : '#EAB308' }}
          />
          {isAdmin ? 'Users Management' : "Today's Activity"}
        </h2>
        
        {!isAdmin ? (
          <div className="flex items-start gap-6">
            {/* Contact Section */}
            <div className="flex-1">
              <div className="text-lg font-semibold text-gray-700 mb-4 px-2">Contacts</div>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {contactActivityData.map((activity, index) => (
                  <ActivityCard key={`contact-${index}`} {...activity} />
                ))}
              </div>
            </div>
            
            {/* Separator */}
            <div className="flex flex-col items-center py-8">
              <div className="w-px h-full bg-gray-300 min-h-[200px]"></div>
            </div>
            
            {/* Leads Section */}
            <div className="flex-1">
              <div className="text-lg font-semibold text-gray-700 mb-4 px-2">Leads</div>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {leadActivityData.map((activity, index) => (
                  <ActivityCard key={`lead-${index}`} {...activity} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <MonthYearFilter 
              selectedMonth={selectedMonth}
              onMonthChange={handleMonthChange}
            />
            <UsersTable 
              users={usersData.users} 
              loading={usersLoading} 
              onSetKPI={handleSetKPI}
            />
            <Pagination 
              currentPage={currentPage}
              totalPages={usersData.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      {/* KPI Modal */}
      <KPIModal
        isOpen={isKPIModalOpen}
        onClose={handleCloseKPIModal}
        selectedUser={selectedUserForKPI}
        selectedMonth={selectedMonth}
        users={usersData.users}
        onSuccess={handleKPISuccess} 
      />
    </div>
  );
};

export default Dashboard;